import React, { useState, useMemo } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import EmptyState from '../empty-state';

/**
 * Generic sortable table following the site's table conventions (zebra rows,
 * gray header, loading skeleton, empty state).
 *
 * columns: [{ key, header, sortable = true, render(value, row), cellProps }]
 *   cellProps: optional extra props (e.g. mouse handlers) spread onto every
 *   <th>/<td> in that column -- lets a column react to being hovered as a whole.
 * rowClassName(row, rowIndex): optional extra classes per row
 */
const DataTable = ({
	columns,
	data,
	loading = false,
	emptyHeading = 'No data available',
	emptyMessage = '',
	rowClassName = null,
}) => {
	const [sortColumn, setSortColumn] = useState(null);
	const [sortDirection, setSortDirection] = useState(null); // 'asc', 'desc', or null

	const handleColumnClick = (column) => {
		if (column.sortable === false) return;
		if (sortColumn === column.key) {
			// Same column clicked - cycle through: asc -> desc -> null
			if (sortDirection === 'asc') {
				setSortDirection('desc');
			} else if (sortDirection === 'desc') {
				setSortColumn(null);
				setSortDirection(null);
			} else {
				setSortDirection('asc');
			}
		} else {
			setSortColumn(column.key);
			setSortDirection('asc');
		}
	};

	const sortedData = useMemo(() => {
		if (!sortColumn || !sortDirection || !data) return data;

		return [...data].sort((a, b) => {
			let aValue = a[sortColumn];
			let bValue = b[sortColumn];

			if (aValue == null && bValue == null) return 0;
			if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
			if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

			if (sortColumn.toLowerCase().includes('date')) {
				aValue = new Date(aValue);
				bValue = new Date(bValue);
			} else if (typeof aValue === 'string' && typeof bValue === 'string') {
				aValue = aValue.toLowerCase();
				bValue = bValue.toLowerCase();
			}

			if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
			if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
			return 0;
		});
	}, [data, sortColumn, sortDirection]);

	const headerCell = (column, index) => (
		<th
			key={index}
			className={`py-2 px-4 font-medium text-gray-500 text-sm select-none ${
				column.align === 'right' ? 'text-right' : 'text-left'
			} ${column.sortable === false ? '' : 'cursor-pointer hover:bg-gray-50'}`}
			onClick={() => handleColumnClick(column)}
			{...column.cellProps}
		>
			<div className={`flex items-center ${column.align === 'right' ? 'justify-end' : 'justify-between'}`}>
				<span>{column.header}</span>
				{sortColumn === column.key && (
					<span className='ml-1'>
						{sortDirection === 'asc' ? (
							<ChevronUpIcon className='h-4 w-4' />
						) : (
							<ChevronDownIcon className='h-4 w-4' />
						)}
					</span>
				)}
			</div>
		</th>
	);

	if (loading) {
		return (
			<div className='overflow-x-auto'>
				<table className='w-full'>
					<thead>
						<tr className='border-b border-gray-200'>
							{columns.map(headerCell)}
						</tr>
					</thead>
					<tbody>
						{[1, 2, 3].map((row) => (
							<tr key={row} className='border-b border-gray-100'>
								{columns.map((column, index) => (
									<td key={index} className='py-4 px-4'>
										<div className='h-4 bg-gray-200 rounded animate-pulse w-full'></div>
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	if (!data || !Array.isArray(data) || data.length === 0) {
		return (
			<EmptyState
				heading={emptyHeading}
				subhead={emptyMessage}
				buttonText=''
				size='sm'
				onClickProperty=''
			/>
		);
	}

	return (
		<div className='overflow-x-auto'>
			<table className='w-full'>
				<thead>
					<tr className='border-b border-gray-200'>{columns.map(headerCell)}</tr>
				</thead>
				<tbody>
					{sortedData.map((row, rowIndex) => (
						<tr
							key={rowIndex}
							className={`border-b border-gray-100 ${
								rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'
							} hover:bg-gray-100 ${rowClassName ? rowClassName(row, rowIndex) : ''}`}
						>
							{columns.map((column, columnIndex) => (
								<td
									key={columnIndex}
									className={`py-2 px-4 text-sm text-gray-500 ${column.align === 'right' ? 'text-right' : ''}`}
									{...column.cellProps}
								>
									{column.render
										? column.render(row[column.key], row)
										: row[column.key]}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

export default DataTable;
